import type { Feature, Polygon, MultiPolygon } from 'geojson';
import rewind from '@turf/rewind';
import { Poly } from '../../types/types';
import { GeoDataUtilities } from '@rogoag/shared-ts';

export function polyContainsGeometry(poly: Poly, geometry: Feature<Polygon>): boolean {
  const fullPolyGeom = polyToPolygon(poly);

  return GeoDataUtilities.firstPolygonContainsSecondPolygon(fullPolyGeom, geometry);
}

export function polyContainsPoly(polyA: Poly, polyB: Poly): boolean {
  const polyAGeom = polyToPolygon(polyA);
  const polyBGeom = polyToPolygon(polyB);

  return GeoDataUtilities.firstPolygonContainsSecondPolygon(polyAGeom, polyBGeom);
}

export function polyOverlapsGeometry(poly: Poly, geometry: Feature<Polygon | MultiPolygon>): boolean {
  const fullPolyGeom = polyToPolygon(poly);

  return GeoDataUtilities.polygonOverlapsPolygon(fullPolyGeom, geometry).overlaps;
}

export function polyOverlapsPoly(polyA: Poly, polyB: Poly): boolean {
  const polyAGeom = polyToPolygon(polyA);
  const polyBGeom = polyToPolygon(polyB);

  return GeoDataUtilities.polygonOverlapsPolygon(polyAGeom, polyBGeom).overlaps;
}

export function polyOuterBoundaryContainsOtherPolyHoles(polyA: Poly, polyB: Poly): boolean {
  let out = false;

  const polyAOuterBoundaryGeom: Feature<Polygon> = {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [polyA.outerBoundary.coordinates.map((c) => c.slice().reverse())],
    },
    properties: {},
  };

  for (const hole of polyB.innerBoundaries) {
    const holeGeom: Feature<Polygon> = {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [hole.coordinates.map((c) => c.slice().reverse())],
      },
      properties: {},
    };

    const doesContain = GeoDataUtilities.firstPolygonContainsSecondPolygon(polyAOuterBoundaryGeom, holeGeom);

    if (doesContain) {
      out = true;
      break;
    }
  }

  return out;
}

export function polyOverlapsOtherPolyHoles(polyA: Poly, polyB: Poly): boolean {
  const polyAGeom = polyToPolygon(polyA);
  let out = false;

  for (const hole of polyB.innerBoundaries) {
    const holeGeom: Feature<Polygon> = {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [hole.coordinates.map((c) => c.slice().reverse())],
      },
      properties: {},
    };

    const doesOverlap = GeoDataUtilities.polygonOverlapsPolygon(polyAGeom, holeGeom).overlaps;

    if (doesOverlap) {
      out = true;
      break;
    }
  }

  return out;
}

function polyToPolygon(poly: Poly): Feature<Polygon> {
  const outerBoundaryCoords = poly.outerBoundary.coordinates.map((c) => c.slice().reverse());
  const innerBoundariesCoords = poly.innerBoundaries.map((ib) => ib.coordinates.map((c) => c.slice().reverse()));

  const polygonFeature: Feature<Polygon> = {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [outerBoundaryCoords, ...innerBoundariesCoords],
    },
    properties: {},
  };

  // Automatically fix winding order: outer CCW, holes CW
  return rewind(polygonFeature, { reverse: false }) as Feature<Polygon>;
}
